// OrderDetail.jsx - One order, everything about it, on six tabs.
//
// The header is the part that matters: it carries the status, the money, and the
// single next action for whatever state the order is in. Somebody working a queue
// should be able to land here, do the obvious thing, and leave.
//
// LEGEND
//   1. NEXT_ACTION   the one button that matters, per status
//   2. Header        who, where, how much, and that button
//   3. Overview      an open cover request, customer, vetting, declared items,
//                    payout, identity, and every agreement accepted
//   4. Media         upload, and the customer visibility switch
//   5. Shipping      the kit as a pair, the return leg, and tracking
//   6. Log           every event
//   7. OrderDetail   the page

import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router";
import clsx from "clsx";

import { useAuth } from "./AdminApp.jsx";
import { useAsync, useForm } from "../lib/hooks.js";
import { adminApi, ordersApi } from "../lib/api.js";
import { money, moneyExact, grams, phone, day, dayTime, ago, titleCase, addressLines, METAL_LABELS, PAYOUT_LABELS, TEST_LABELS, HEARD_LABELS } from "../lib/format.js";
import { Button, Panel, Tabs, Tag, Alert, Loading, Modal, Input, Textarea, Select, SelectOrType, Checkbox, Empty, CopyButton, useToast } from "../ui/kit.jsx";
import { PROVIDERS, carriersFor, servicesFor, packagesFor, equivalentFor } from "../lib/carriers.js";
import { ArrowLeft, ArrowUpRight, Truck, Camera, Note, Message, Trash, Refresh, Plus, Printer, Flag, IdCard, Dollar, Phone, Envelope, Upload, Check, Shield, Pencil } from "../ui/icons.jsx";

import Appraisal from "./Appraisal.jsx";
import OrderMessages from "./OrderMessages.jsx";

////////// 1. NEXT ACTION //////////
// Keyed by status. `to` is a tab, `action` is a dialog.

const NEXT_ACTION = {
  new: { label: "Review and approve", action: "status", to: "overview", hint: "Decide whether to send a kit" },
  vetting: { label: "Approve and send a kit", action: "status", to: "overview", hint: "Flagged during vetting" },
  approved: { label: "Buy the kit labels", action: "label", to: "shipping", hint: "Nothing has shipped yet" },
  kit_sent: { label: "Check tracking", action: "refresh", to: "shipping", hint: "Kit is on its way" },
  kit_delivered: { label: "Waiting on the customer", action: null, to: "shipping", hint: "They have the kit" },
  in_transit: { label: "Check tracking", action: "refresh", to: "shipping", hint: "Items are coming to us" },
  received: { label: "Film the opening", action: "upload", to: "media", hint: "Record it before touching anything" },
  evaluating: { label: "Weigh and quote", action: null, to: "appraisal", hint: "On the bench" },
  offer_made: { label: "Waiting on the customer", action: null, to: "appraisal", hint: "Offer is out" },
  accepted: { label: "Pay them", action: "payout", to: "overview", hint: "They said yes" },
  declined: { label: "Buy a return label", action: "label", to: "shipping", hint: "Send it all back, insured, our cost" },
  paid: { label: "Closed", action: null, to: "overview" },
  returned: { label: "Closed", action: null, to: "overview" },
  cancelled: { label: "Closed", action: null, to: "overview" },
};

const STATUS_TONE = {
  new: "caution", vetting: "caution", received: "critical", accepted: "positive",
  declined: "caution", paid: "positive", offer_made: "bronze",
};

////////// SMALL PIECES //////////

const Row = ({ label, children, className }) => (
  <div className={clsx("flex items-baseline justify-between gap-4 py-2", className)}>
    <dt className="text-[13px] text-muted shrink-0">{label}</dt>
    <dd className="text-[13.5px] text-ink text-right min-w-0 break-words">{children ?? "—"}</dd>
  </div>
);

// The proof that this customer accepted what they accepted. Everything here is
// stamped by the server, so it is evidence rather than something the browser
// claimed. If a dispute ever turns on "they never agreed to that", this panel is
// the answer, and it is why the network address and browser are shown in full.
function Agreements({ rows }) {
  if (!rows?.length) return null;

  return (
    <Panel className="p-4">
      <p className="eyebrow mb-3">Agreements accepted</p>
      <ol className="space-y-3.5">
        {rows.map((row, index) => (
          <li key={`${row.key}-${row.at}-${index}`} className="pb-3.5 border-b border-rule-soft last:border-0 last:pb-0">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <a
                href={row.path}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13.5px] font-semibold text-ink link"
              >
                {row.title}
              </a>
              <Tag>v{row.version}</Tag>
            </div>
            <p className="text-[12.5px] text-muted mt-1.5">
              {dayTime(row.at)} &middot; from {row.ip || "an unknown address"}
            </p>
            {row.prompt && (
              <p className="text-[12px] text-faint mt-1.5 leading-relaxed italic">&ldquo;{row.prompt}&rdquo;</p>
            )}
            {row.userAgent && (
              <p className="text-[11.5px] text-faint mt-1 break-words">{row.userAgent}</p>
            )}
          </li>
        ))}
      </ol>
    </Panel>
  );
}

////////// EDIT THE ORDER //////////
// Everything about an order that is a fact rather than a decision. Names,
// address, what they said they were sending, and the internal fields nobody but
// staff sees.
//
// What is deliberately not in here, and why, because the absence is the design:
//
//   The status has its own dialog, because moving an order messages the
//   customer. The offer, the payout and the cover request have their own routes
//   for the same reason: each does something besides write a field.
//
//   Text consent is on the messages tab and only there. Turning it on records an
//   agreement saying how it was obtained and who took it, and a checkbox sitting
//   in an edit form would set it with none of that. Consent nobody can account
//   for is worth less than no consent at all, because it looks like something.
//
// The address is the field worth being careful about. A label that is already
// bought carries the address it was bought with, so changing it here changes the
// order and changes nothing about a parcel that is already moving. That is not
// refused, because somebody correcting an address is usually doing it precisely
// because the parcel went astray, but it is said out loud before saving and
// again on the timeline afterwards.

const ITEM_ROW = { description: "", metal: "gold", karat: "", quantity: 1, approxWeight: "", unit: "g", note: "" };

function EditDialog({ order, open, onClose, onDone }) {
  const toast = useToast();
  const { data: team } = useAsync(() => adminApi.users(), [], { skip: !open });

  // Rebuilt whenever the dialog opens, so a second edit starts from what is
  // actually on the order rather than from whatever was typed and abandoned.
  const form = useForm({});
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!open) return;
    form.setValues({
      firstName: order.customer?.firstName || "",
      lastName: order.customer?.lastName || "",
      email: order.customer?.email || "",
      phone: order.customer?.phone || "",
      line1: order.customer?.address?.line1 || "",
      line2: order.customer?.address?.line2 || "",
      city: order.customer?.address?.city || "",
      state: order.customer?.address?.state || "",
      postal: order.customer?.address?.postal || "",
      tags: (order.tags || []).join(", "),
      internalNotes: order.internalNotes || "",
      assignedTo: order.assignedTo || "",
    });
    setItems((order.declared || []).map((item) => ({
      description: item.description || "",
      metal: item.metal || "gold",
      karat: item.karat || "",
      quantity: item.quantity ?? 1,
      approxWeight: item.approxWeight ?? "",
      unit: item.unit || "g",
      note: item.note || "",
    })));
    // form.setValues is stable and order identity is what should retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order]);

  // Which legs are already travelling on the address as it stands. A voided
  // label is not one of them, and neither is a leg bought but never scanned.
  const travelling = (order.shipments || [])
    .filter((shipment) => !shipment.voidedAt && shipment.tracking)
    .map((shipment) => (LEG_TITLES[shipment.leg] || shipment.leg.replace(/_/g, " ")).toLowerCase());

  const addressTouched = ["line1", "line2", "city", "state", "postal"].some((key) => {
    const was = order.customer?.address?.[key] || "";
    return String(form.values[key] ?? "").trim() !== String(was).trim();
  });

  const setItem = (index, patch) =>
    setItems((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const save = form.submit(async (values) => {
    const updated = await ordersApi.update(order.number, {
      customer: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        address: {
          line1: values.line1,
          line2: values.line2,
          city: values.city,
          state: values.state,
          postal: values.postal,
        },
      },
      // Empty rows are dropped rather than saved as blanks somebody has to
      // delete later. A row with only a weight is still a row: they know it is
      // ten grams of something.
      declared: items
        .filter((item) => item.description || item.approxWeight || item.note)
        .map((item) => ({
          ...item,
          quantity: Number(item.quantity) || 1,
          approxWeight: item.approxWeight === "" ? undefined : Number(item.approxWeight),
          karat: item.karat || undefined,
        })),
      tags: values.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
      internalNotes: values.internalNotes,
      assignedTo: values.assignedTo || null,
    });

    toast.success("Saved");
    onDone(updated);
    onClose();
    return updated;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Edit ${order.number}`}
      description="Everything here is a fact about the order. The status, the offer and the payout have their own buttons, and text consent is recorded on the messages tab."
      footer={<><Button variant="quiet" onClick={onClose}>Cancel</Button><Button onClick={save} loading={form.busy}>Save changes</Button></>}
    >
      <form onSubmit={save} noValidate className="space-y-6">
        {form.message && <Alert tone="error">{form.message.text}</Alert>}

        <section>
          <p className="eyebrow mb-3">Customer</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="First name" {...form.field("firstName")} />
            <Input label="Last name" {...form.field("lastName")} />
            <Input label="Email" type="email" {...form.field("email")} />
            <Input label="Phone" {...form.field("phone")} hint="Any format. It is stored the way the carrier sends it." />
          </div>
        </section>

        <section>
          <p className="eyebrow mb-3">Address</p>

          {/* Before saving, not after. Once it is saved the parcel and the order
              disagree and the only person who can decide what to do about it is
              the one who is here now. */}
          {addressTouched && travelling.length > 0 && (
            <Alert tone="warning" className="mb-3" title="A label is already travelling on the old address">
              The {travelling.join(" and ")} {travelling.length === 1 ? "was" : "were"} bought with the address as it
              stands, and printed paper cannot be changed from here. Saving updates the order and the record, not the
              parcel. If it has not gone yet, void the label and buy it again afterwards.
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Street address" wrapClassName="sm:col-span-2" {...form.field("line1")} />
            <Input label="Apartment, suite" wrapClassName="sm:col-span-2" {...form.field("line2")} />
            <Input label="City" {...form.field("city")} />
            <div className="grid grid-cols-2 gap-4">
              <Input label="State" maxLength={2} {...form.field("state")} />
              <Input label="ZIP" {...form.field("postal")} />
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <p className="eyebrow">What they said they were sending</p>
              <p className="hint mt-0.5">Their description, not ours. The appraisal is what we weighed.</p>
            </div>
            <Button size="sm" variant="quiet" type="button" onClick={() => setItems((rows) => [...rows, { ...ITEM_ROW }])}>
              <Plus size={14} /> Add
            </Button>
          </div>

          {items.length === 0 ? (
            <p className="hint">Nothing listed.</p>
          ) : (
            <div className="space-y-3">
              {items.map((item, index) => (
                <div key={index} className="grid gap-2 sm:grid-cols-[1fr_auto] items-start border border-rule-soft p-3">
                  <div className="grid gap-2 sm:grid-cols-4">
                    <Input
                      label="Description"
                      wrapClassName="sm:col-span-4"
                      value={item.description}
                      onChange={(event) => setItem(index, { description: event.target.value })}
                    />
                    <Select
                      label="Metal"
                      options={Object.entries(METAL_LABELS).map(([value, label]) => ({ value, label }))}
                      value={item.metal}
                      onChange={(event) => setItem(index, { metal: event.target.value })}
                    />
                    <Input label="Karat" value={item.karat} onChange={(event) => setItem(index, { karat: event.target.value })} />
                    <Input
                      label="Quantity" type="number" min="1"
                      value={item.quantity}
                      onChange={(event) => setItem(index, { quantity: event.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        label="Weight" type="number" min="0" step="0.01"
                        value={item.approxWeight}
                        onChange={(event) => setItem(index, { approxWeight: event.target.value })}
                      />
                      <Select
                        label="Unit"
                        options={[{ value: "g", label: "g" }, { value: "dwt", label: "dwt" }, { value: "ozt", label: "ozt" }]}
                        value={item.unit}
                        onChange={(event) => setItem(index, { unit: event.target.value })}
                      />
                    </div>
                  </div>
                  <Button
                    size="sm" variant="ghost" type="button"
                    className="sm:mt-6"
                    aria-label="Remove this item"
                    onClick={() => setItems((rows) => rows.filter((_, i) => i !== index))}
                  >
                    <Trash size={15} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <p className="eyebrow mb-3">Ours, not theirs</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Assigned to"
              options={[{ value: "", label: "Nobody" }, ...(team?.users || []).map((user) => ({ value: user._id, label: user.name }))]}
              {...form.field("assignedTo")}
            />
            <Input label="Tags" hint="Separated by commas." {...form.field("tags")} />
          </div>
          <Textarea
            label="Internal notes"
            rows={4}
            wrapClassName="mt-4"
            hint="The customer never sees this. Notes on the timeline are the ones that can be shown to them."
            {...form.field("internalNotes")}
          />
        </section>
      </form>
    </Modal>
  );
}

function StatusDialog({ order, open, onClose, onDone }) {
  const toast = useToast();
  const [status, setStatus] = useState(order.status);
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      onDone(await ordersApi.setStatus(order.number, { status, note: note || undefined, notify }));
      toast.success(`Moved to ${status.replace(/_/g, " ")}`);
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change the status"
      description="Some statuses send the customer a message. You can turn that off."
      footer={<><Button variant="quiet" onClick={onClose}>Cancel</Button><Button onClick={apply} loading={busy}>Apply</Button></>}
    >
      <div className="space-y-4">
        <Select
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          options={Object.keys(NEXT_ACTION).map((key) => ({ value: key, label: titleCase(key) }))}
        />
        <Textarea
          label="Note for the timeline"
          rows={2}
          placeholder="Optional. The customer sees this."
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <Checkbox
          label="Tell the customer"
          hint="Sends the template for this status by email, and by text if they consented."
          checked={notify}
          onChange={(event) => setNotify(event.target.checked)}
        />
      </div>
    </Modal>
  );
}

function PayoutDialog({ order, open, onClose, onDone }) {
  const toast = useToast();
  const form = useForm({
    method: order.payout?.method || "ach",
    amount: order.offer?.amount || "",
    reference: "",
    notify: true,
  });

  const submit = form.submit(async (values) => {
    const updated = await ordersApi.payout(order.number, { ...values, amount: Number(values.amount) });
    toast.success(`Marked ${money(Number(values.amount))} as paid`);
    onDone(updated);
    onClose();
    return updated;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record the payout"
      description="This closes the order and tells the customer their money is on the way."
      footer={<><Button variant="quiet" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={form.busy}>Mark as paid</Button></>}
    >
      <div className="space-y-4">
        {order.payout?.details && Object.keys(order.payout.details).length > 0 && (
          <Panel quiet className="p-4">
            <p className="eyebrow mb-2">What the customer gave us</p>
            <dl className="text-[13px]">
              {Object.entries(order.payout.details).map(([key, value]) => (
                <Row key={key} label={titleCase(key)}>{String(value)}</Row>
              ))}
            </dl>
            <p className="hint mt-2 pt-2 border-t border-rule">
              Account numbers are masked. Full details are on the overview tab, and looking at them is logged.
            </p>
          </Panel>
        )}

        <Select
          label="Method"
          options={Object.entries(PAYOUT_LABELS).map(([value, label]) => ({ value, label }))}
          {...form.field("method")}
        />
        <Input label="Amount" type="number" step="1" prefix="$" className="pl-7" required {...form.field("amount")} />
        <Input label="Reference" placeholder="Transfer or check number" {...form.field("reference")} />
        <Checkbox
          label="Tell the customer it is on the way"
          checked={form.values.notify}
          onChange={(event) => form.set("notify", event.target.checked)}
        />
        {form.message && <Alert tone="error">{form.message.text}</Alert>}
      </div>
    </Modal>
  );
}

////////// COVER REQUEST //////////
// Somebody asked us to be liable for more than the standard amount, and they
// have been told to hold their package until we answer. So this sits at the top
// of the overview, not in a tab, and it says what they are waiting on.

function CoverageCard({ order, onChanged }) {
  const toast = useToast();
  const coverage = order.coverage;
  const [amount, setAmount] = useState(coverage?.requestedValue ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!coverage?.status || coverage.status === "none") return null;

  const decide = async (decision) => {
    setBusy(true);
    try {
      onChanged(await ordersApi.decideCoverage(order.number, {
        decision,
        approvedValue: decision === "approved" ? Number(amount) : undefined,
        reason: reason.trim() || undefined,
        notify: true,
      }));
      toast.success(decision === "approved" ? "Approved, and they have been told they can send" : "Denied, and they have been told the standard cover stands");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (coverage.status !== "requested") {
    const approved = coverage.status === "approved";
    return (
      <Panel className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield size={16} className={approved ? "text-bronze" : "text-faint"} />
            <p className="text-[14px] text-ink">
              {approved
                ? <>Covered to <strong>{money(coverage.approvedValue)}</strong> on this shipment</>
                : <>Higher cover denied. The standard amount stands.</>}
            </p>
          </div>
          <p className="text-[12.5px] text-faint">
            {coverage.decidedBy || "Somebody"} · {coverage.decidedAt ? day(coverage.decidedAt) : ""}
            {coverage.tracking ? <span className="num"> · {coverage.tracking}</span> : null}
          </p>
        </div>
        {coverage.reason && <p className="hint mt-2">{coverage.reason}</p>}
      </Panel>
    );
  }

  return (
    <Panel className="p-5 border-caution">
      <div className="flex items-start gap-2.5">
        <Shield size={18} className="text-caution shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[15px] text-ink">
            Asked us to cover this for {money(coverage.requestedValue)}
          </p>
          <p className="hint mt-1">
            Requested {coverage.requestedAt ? ago(coverage.requestedAt) : ""}. They have been told to hold their
            package until we answer, so this is somebody waiting on us.
          </p>

          {coverage.description && (
            <p className="mt-3 text-[13.5px] text-ink-soft leading-relaxed whitespace-pre-line border-l border-rule pl-3">
              {coverage.description}
              {coverage.pieces ? `\n\nAbout ${coverage.pieces} pieces.` : ""}
            </p>
          )}

          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <Input
              label="Approve up to"
              type="number"
              step="500"
              prefix="$"
              className="pl-7"
              hint="Approving less than they asked for is normal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
            <Input
              label="Note to them"
              placeholder="Optional, goes in the message"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button size="sm" onClick={() => decide("approved")} loading={busy}>
              <Check size={14} /> Approve {money(Number(amount) || coverage.requestedValue)}
            </Button>
            <Button size="sm" variant="quiet" onClick={() => decide("denied")} loading={busy}>
              Deny
            </Button>
          </div>
        </div>
      </div>
    </Panel>
  );
}

////////// 3. OVERVIEW //////////

function Overview({ order, onChanged, onEdit }) {
  const { can } = useAuth();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [shareNote, setShareNote] = useState(false);
  const [fullDetails, setFullDetails] = useState(null);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await ordersApi.addNote(order.number, { text: note, type: noteType, shareWithCustomer: shareNote });
      setNote("");
      setShareNote(false);
      toast.success("Added to the log");
      onChanged();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const revealDetails = async () => {
    try {
      setFullDetails(await ordersApi.payoutDetails(order.number));
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    // Two things about this grid are load bearing. `minmax(0,1fr)` rather than
    // `1fr`, because a plain `1fr` track is allowed to grow to its content's
    // minimum width, and the appraised table under it asks for 600px: the track
    // took it, the row got wider than the page, and `overflow-x: clip` on the
    // body threw the side column away with no scrollbar to get it back. And xl
    // rather than lg, because the sidebar is 228px, so a 1024px window has 732px
    // to divide and 340px of that is not a side column, it is most of the page.
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="space-y-5 min-w-0">
        <CoverageCard order={order} onChanged={onChanged} />

        {/* Log a note or a call. The most used control on this page. */}
        <Panel className="p-4">
          <Textarea
            label="Add to the log"
            rows={3}
            placeholder="What happened, what was said, what to do next."
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5 mt-3">
            <div className="flex gap-1">
              {[["note", "Note"], ["call", "Call"]].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setNoteType(value)}
                  className={clsx("btn btn-sm", noteType === value ? "btn-ink" : "btn-quiet")}
                >
                  {label}
                </button>
              ))}
            </div>
            <Checkbox label="Show this to the customer" checked={shareNote} onChange={(event) => setShareNote(event.target.checked)} />
            <Button size="sm" onClick={addNote} disabled={!note.trim()} className="ml-auto max-xs:w-full">Save</Button>
          </div>
        </Panel>

        {/* What they said they were sending */}
        {order.declared?.length > 0 && (
          <Panel>
            <p className="eyebrow px-4 py-3 border-b border-rule">What they described</p>
            <ul className="divide-y divide-rule-soft">
              {order.declared.map((item, index) => (
                <li key={index} className="px-4 py-2.5 flex items-baseline justify-between gap-4 text-[13.5px]">
                  <span className="text-ink">{item.description || METAL_LABELS[item.metal]}</span>
                  <span className="num text-muted shrink-0">
                    {item.quantity > 1 ? `${item.quantity} × ` : ""}
                    {METAL_LABELS[item.metal]}{item.karat ? ` ${item.karat.toUpperCase()}` : ""}
                    {item.approxWeight ? ` · ~${item.approxWeight}${item.unit}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {/* What we actually weighed */}
        {order.appraised?.length > 0 && (
          <Panel>
            <p className="eyebrow px-4 py-3 border-b border-rule">Appraised</p>
            <div className="sm:overflow-x-auto">
              <table className="grid-table stack-table w-full sm:min-w-[600px]">
                <thead>
                  <tr>
                    <th>Lot</th><th>Purity</th><th className="text-right">Gross</th>
                    <th className="text-right">Stones</th><th className="text-right">Fine</th><th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {order.appraised.map((lot) => (
                    <tr key={lot.id}>
                      <td data-label="Lot">
                        <span className="text-ink">{lot.label || METAL_LABELS[lot.metal]}</span>
                        <span className="block text-[12px] text-faint">{METAL_LABELS[lot.metal]} · {TEST_LABELS[lot.testMethod] || lot.testMethod}</span>
                      </td>
                      <td className="num" data-label="Purity">{lot.purity?.toUpperCase() || "—"}</td>
                      <td className="text-right num" data-label="Gross">{grams(lot.grossWeight)}</td>
                      <td className="text-right num text-muted stack-hide" data-label="Stones">{lot.stoneWeight ? grams(lot.stoneWeight) : "—"}</td>
                      <td className="text-right num" data-label="Fine">{grams(lot.fineWeight, 3)}</td>
                      <td className="text-right num font-semibold" data-label="Amount">{money(lot.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {/* Identity, when the customer has submitted it */}
        {order.compliance?.submittedAt && (
          <Panel>
            <p className="eyebrow px-4 py-3 border-b border-rule flex items-center gap-2">
              <IdCard size={15} /> Identity and ownership
            </p>
            <div className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[["Front", order.identity?.idFront], ["Back", order.identity?.idBack], ["Signature", order.identity?.signature]].map(([label, url]) => (
                  <div key={label}>
                    <p className="eyebrow mb-1.5">{label}</p>
                    {url ? (
                      <a href={url} target="_blank" rel="noopener noreferrer" className="block border border-rule bg-surface aspect-3/2 overflow-hidden">
                        <img src={url} alt={label} className="w-full h-full object-contain" />
                      </a>
                    ) : (
                      <div className="border border-dashed border-rule aspect-3/2 flex items-center justify-center text-[12px] text-faint">
                        Missing
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <dl className="mt-4 pt-3 border-t border-rule-soft">
                <Row label="Declared ownership">{order.compliance.ownershipDeclared ? "Yes" : "No"}</Row>
                <Row label="Signed">{order.compliance.signatureType === "type" ? order.compliance.signatureText : "Drawn"}</Row>
                <Row label="Submitted">{dayTime(order.compliance.submittedAt)}</Row>
                <Row label="From">{order.compliance.ip}</Row>
              </dl>
            </div>
          </Panel>
        )}

        <Agreements rows={order.agreements} />
      </div>

      {/* Side column */}
      <div className="space-y-5 min-w-0">
        <Panel className="p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="eyebrow mb-2">Customer</p>
            {/* Here as well as in the header, because this panel is where
                somebody is looking when they notice the ZIP is wrong. */}
            <button
              type="button"
              onClick={onEdit}
              className="text-[12.5px] font-semibold text-muted hover:text-ink -mt-0.5"
            >
              Edit
            </button>
          </div>
          <p className="display display-sm text-ink">{order.customerName || "No name"}</p>
          <dl className="mt-3 divide-y divide-rule-soft">
            <Row label="Email">
              {order.customer.email ? (
                <a href={`mailto:${order.customer.email}`} className="link break-all">{order.customer.email}</a>
              ) : null}
            </Row>
            <Row label="Phone">
              {order.customer.phone ? (
                <a href={`tel:${order.customer.phone}`} className="link num">{phone(order.customer.phone)}</a>
              ) : null}
            </Row>
            <Row label="Texts">
              <Tag tone={order.consent?.sms ? "positive" : "neutral"}>{order.consent?.sms ? "Consented" : "No consent"}</Tag>
            </Row>
            <Row label="Address">
              {order.customer.address?.line1 ? (
                <span className="whitespace-pre-line">
                  {order.customer.address.line1}
                  {order.customer.address.line2 ? `\n${order.customer.address.line2}` : ""}
                  {`\n${order.customer.address.city}, ${order.customer.address.state} ${order.customer.address.postal}`}
                </span>
              ) : null}
            </Row>
            <Row label="Source">{titleCase(order.source)}</Row>
            <Row label="Requested">{day(order.createdAt)}</Row>
          </dl>
        </Panel>

        {/* Where they came from. Two answers to the same question that are
            allowed to disagree: what the customer says, and what the browser
            measured on the way in. The click id knows which ad was touched
            last, which is not the same thing as what sent somebody, and the
            campaign that gets the credit is often the one that only closed it.
            Neither line is shown as the truth. */}
        {(order.heardAbout?.source || order.attribution?.source || order.attribution?.referrer) && (
          <Panel className="p-4">
            <p className="eyebrow mb-2">Where they came from</p>
            <dl>
              {order.heardAbout?.source && (
                <Row label="They said">
                  {HEARD_LABELS[order.heardAbout.source] || titleCase(order.heardAbout.source)}
                  {order.heardAbout.detail ? <span className="block hint mt-0.5">{order.heardAbout.detail}</span> : null}
                </Row>
              )}
              {order.attribution?.source && (
                <Row label="Measured">
                  {[order.attribution.source, order.attribution.medium].filter(Boolean).join(" / ")}
                  {order.attribution.campaign ? <span className="block hint mt-0.5">{order.attribution.campaign}</span> : null}
                </Row>
              )}
              {order.attribution?.referrer && <Row label="Came from">{order.attribution.referrer}</Row>}
              {order.attribution?.landing && <Row label="Landed on">{order.attribution.landing}</Row>}
            </dl>
          </Panel>
        )}

        {/* Vetting, only while it still matters */}
        {(order.vetting?.score > 0 || order.vetting?.flags?.length > 0) && (
          <Panel className="p-4">
            <p className="eyebrow mb-2">Vetting</p>
            <div className="flex items-baseline gap-3">
              <span className="display display-md num text-ink">{order.vetting.score}</span>
              <span className="hint">out of 100</span>
            </div>
            {order.vetting.flags?.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {order.vetting.flags.map((flag) => (
                  <li key={flag} className="flex gap-2 text-[13px] text-caution">
                    <Flag size={14} className="shrink-0 mt-0.5" /> {flag}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )}

        {/* Money */}
        <Panel className="p-4">
          <p className="eyebrow mb-2">Money</p>
          <dl className="divide-y divide-rule-soft">
            <Row label="Offer">{order.offer?.amount ? money(order.offer.amount) : null}</Row>
            <Row label="Sent">{order.offer?.madeAt ? day(order.offer.madeAt) : null}</Row>
            <Row label="Answer">
              {order.offer?.response
                ? <Tag tone={order.offer.response === "accepted" ? "positive" : "caution"}>{order.offer.response}</Tag>
                : order.offer?.amount ? <Tag tone="bronze">waiting</Tag> : null}
            </Row>
            <Row label="Paid">{order.payout?.sentAt ? `${money(order.payout.amount)} on ${day(order.payout.sentAt)}` : null}</Row>
            <Row label="Method">{order.payout?.method ? PAYOUT_LABELS[order.payout.method] : null}</Row>
          </dl>

          {order.payout?.details && Object.keys(order.payout.details).length > 0 && can("orders.payout") && (
            <div className="mt-3 pt-3 border-t border-rule-soft">
              <p className="eyebrow mb-2">Payout details</p>
              <dl className="text-[13px]">
                {Object.entries(fullDetails || order.payout.details).map(([key, value]) => (
                  <Row key={key} label={titleCase(key)}>
                    <span className="num">{String(value)}</span>
                  </Row>
                ))}
              </dl>
              {!fullDetails && (
                <Button size="sm" variant="quiet" className="mt-2 w-full" onClick={revealDetails}>
                  Reveal full numbers
                </Button>
              )}
            </div>
          )}
        </Panel>

        {/* The customer's own view */}
        <Panel quiet className="p-4">
          <p className="eyebrow mb-2">Their tracking page</p>
          <p className="hint break-all num text-[12px]">{order.trackingUrl}</p>
          <div className="flex gap-2 mt-2">
            <CopyButton value={order.trackingUrl} label="Copy link" />
            <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">
              Open <ArrowUpRight size={14} />
            </a>
          </div>
        </Panel>
      </div>
    </div>
  );
}

////////// 4. MEDIA //////////

function Media({ order, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [visibility, setVisibility] = useState("customer");

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      await ordersApi.uploadMedia(order.number, files, { visibility, folder: "items" });
      toast.success(`Uploaded ${files.length} ${files.length === 1 ? "file" : "files"}`);
      onChanged();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (index, next) => {
    try {
      onChanged(await ordersApi.updateMedia(order.number, index, { visibility: next }));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const remove = async (index) => {
    try {
      onChanged(await ordersApi.deleteMedia(order.number, index));
      toast.success("Deleted");
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="space-y-5">
      <Panel className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className={clsx("btn", busy ? "btn-quiet" : "btn-primary", busy && "opacity-60")}>
            <Upload size={16} /> {busy ? "Uploading" : "Upload photos or video"}
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              className="sr-only"
              disabled={busy}
              onChange={(event) => upload([...event.target.files])}
            />
          </label>

          <div className="flex gap-1">
            {[["customer", "Customer can see"], ["internal", "Internal only"]].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setVisibility(value)}
                className={clsx("btn btn-sm", visibility === value ? "btn-ink" : "btn-quiet")}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="hint mt-3">
          The unboxing video is the whole reason customers trust this. Upload it before anything else,
          set to customer visible, and it appears on their page immediately.
        </p>
      </Panel>

      {order.media?.length ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {order.media.map((item) => (
            <Panel key={item.key} className="overflow-hidden">
              <div className="aspect-4/3 bg-surface flex items-center justify-center">
                {item.kind === "video" ? (
                  <video src={item.url} controls preload="metadata" className="w-full h-full object-cover" />
                ) : (
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                    <img src={item.url} alt={item.label} className="w-full h-full object-cover" loading="lazy" />
                  </a>
                )}
              </div>
              <div className="p-3">
                <p className="text-[13px] text-ink truncate">{item.label}</p>
                <p className="text-[11.5px] text-faint mt-0.5">
                  {item.by} &middot; {ago(item.at)} &middot; {Math.round((item.size || 0) / 1024)} KB
                </p>
                <div className="flex items-center gap-2 mt-2.5">
                  <button
                    type="button"
                    onClick={() => toggle(item.index, item.visibility === "customer" ? "internal" : "customer")}
                    className={clsx("tag cursor-pointer", item.visibility === "customer" ? "tag-positive" : "")}
                  >
                    {item.visibility === "customer" ? "Customer sees this" : "Internal only"}
                  </button>
                  <button type="button" onClick={() => remove(item.index)} className="btn btn-ghost btn-sm ml-auto !px-1.5" aria-label="Delete">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </Panel>
          ))}
        </div>
      ) : (
        <Empty title="Nothing uploaded yet" icon={Camera}>
          Film the opening before you touch anything in the package.
        </Empty>
      )}
    </div>
  );
}

////////// 5. SHIPPING //////////
// A kit is two labels and they are treated as one thing here, because that is
// what they are: the mailer going out and the prepaid label inside it. One press
// buys both, and the pair prints on one page.
//
// Nothing asks for an insurance value before printing. Cover is arranged under
// our own policy rather than bought from a carrier, so a label is bought with
// whatever the carrier includes and the declared amount is set from the standard
// cover, or from an approved higher amount if the order has one.
//
// It stays editable on the card below until the parcel moves, because "there is
// more in the box than I said" is a phone call that arrives after the label is
// printed more often than before.

const KIT_LEGS = [
  { leg: "outbound_kit", label: "Kit to the customer", note: "Goes on the outside of the mailer" },
  { leg: "inbound_items", label: "Prepaid return label", note: "Goes inside it, for their items to come back" },
];

// Buying labels the long way, filled in from the templates and changed from
// there.
//
// The templates in settings are a starting point and this is where they stop
// being one. Most kits are identical and never come near this dialog; the
// occasional bigger item goes a different way without anybody editing settings
// for the sake of one order.
//
// A kit is still bought as a pair here, both halves in one press, because two
// labels bought minutes apart is how a return label ends up in the wrong mailer.
// A bigger item usually means both halves change anyway: a box out and a box
// back.
//
// The cover is a number we record against our own policy. It is not sent to
// EasyPost or to Stamps and neither of them underwrites anything.

const LEG_TITLES = {
  outbound_kit: "Kit out to the customer",
  inbound_items: "Prepaid return label",
  return_items: "Items back to the customer",
};

// An empty envelope carries nothing, so it declares nothing. The server agrees;
// this is only what the box opens with.
const coverFor = (leg, order, standard) => {
  if (leg === "outbound_kit") return 0;
  if (order.coverage?.status === "approved") return order.coverage.approvedValue;
  return standard ?? "";
};

const planFrom = (leg, template, order, standard) => ({
  provider: template?.provider || "easypost",
  carrier: template?.carrier || "",
  service: template?.service || "",
  package: template?.package || "",
  weightOz: template?.weightOz ?? 16,
  insuredValue: coverFor(leg, order, standard),
});

// Changing the provider or the carrier invalidates everything under it: a
// FedEx service code means nothing to Stamps, and leaving it there is how
// somebody buys a label with a field the other provider silently ignores. So
// the change cascades, and lands on that provider's first sensible answer
// rather than on nothing.
function planAfter(plan, key, value) {
  // Changing who prints a label is not a reason to lose what was already chosen.
  // Both providers sell USPS, so First Class Mail at Stamps becomes Ground
  // Advantage at EasyPost and the envelope goes to the same place. Only what the
  // new provider genuinely cannot express is replaced.
  if (key === "provider") return { ...plan, provider: value, ...equivalentFor(value, plan) };

  // A carrier change is different: a FedEx service is not a UPS service under
  // another name, so a service the new carrier does not list cannot be kept. One
  // that it does list is left alone.
  if (key === "carrier") {
    const keeps = (list, code) => (list.some((entry) => entry.code === code) ? code : "");
    return {
      ...plan,
      carrier: value,
      service: keeps(servicesFor(plan.provider, value), plan.service),
      package: keeps(packagesFor(plan.provider, value), plan.package),
    };
  }
  return { ...plan, [key]: value };
}

// Anything left blank is left out of the request. The server reads a missing
// field as "whatever the template says", which is what an empty box means here.
const planBody = (plan) => ({
  provider: plan.provider,
  carrier: plan.carrier,
  service: plan.service,
  package: plan.package,
  ...(plan.weightOz === "" ? {} : { weightOz: Number(plan.weightOz) }),
  ...(plan.insuredValue === "" ? {} : { insuredValue: Number(plan.insuredValue) }),
});

// `wide` is the single label case, where the dialog is one column of legs and
// the fields can sit two across. A pair puts each leg in its own column, so its
// fields stack: both halves are then on screen at once, which is the whole
// reason somebody opened this rather than pressing the other button.
function LabelFields({ plan, set, wide }) {
  const stamps = plan.provider === "stamps";
  const span = wide ? "sm:col-span-2" : undefined;
  const provider = PROVIDERS.find((entry) => entry.value === plan.provider);

  return (
    <div className={clsx("grid gap-4", wide && "sm:grid-cols-2")}>
      <Select
        label="Bought from"
        value={plan.provider}
        onChange={(event) => set("provider", event.target.value)}
        options={PROVIDERS}
        hint={provider?.note}
      />
      <SelectOrType
        label="Carrier"
        options={carriersFor(plan.provider)}
        anyLabel={stamps ? undefined : "Any carrier, price them all"}
        value={plan.carrier}
        onChange={(value) => set("carrier", value)}
        hint={stamps ? "Stamps only prints USPS" : undefined}
      />
      <SelectOrType
        label="Service"
        options={servicesFor(plan.provider, plan.carrier)}
        anyLabel={stamps ? undefined : "Cheapest they quote"}
        value={plan.service}
        onChange={(value) => set("service", value)}
        hint={plan.carrier ? undefined : "Pick a carrier first, or leave both blank to price everything"}
        wrapClassName={span}
      />
      <SelectOrType
        label="Package"
        options={packagesFor(plan.provider, plan.carrier)}
        anyLabel="None, our own packaging"
        value={plan.package}
        onChange={(value) => set("package", value)}
      />
      <Input label="Weight" type="number" min="0.1" suffix="oz" value={plan.weightOz} onChange={(event) => set("weightOz", event.target.value)} />
      <Input
        label="Covered for"
        type="number"
        min="0"
        prefix="$"
        value={plan.insuredValue}
        onChange={(event) => set("insuredValue", event.target.value)}
        hint="Ours, and recorded here only"
        wrapClassName={span}
      />
    </div>
  );
}

function BuyLabelDialog({ order, legs, templates, standardCover, open, onClose, onDone }) {
  const toast = useToast();
  const [plans, setPlans] = useState(null);
  // The one refusal with an obvious next move, held in the dialog rather than
  // thrown at a toast that disappears before anybody can act on it.
  const [stuck, setStuck] = useState(null);
  const [busy, setBusy] = useState(false);

  // The templates and the cover are read through a ref rather than listed as
  // dependencies, because settings arrive from their own request: if they land a
  // second after somebody opened this, listing them would wipe what has already
  // been typed. Opening is the only thing that seeds the form.
  const seed = useRef(null);
  seed.current = { templates, standardCover };
  const key = (legs || []).join(",");

  useEffect(() => {
    if (!open || !key) {
      setPlans(null);
      setStuck(null);
      return;
    }
    // A refusal belongs to the attempt that caused it, not to the next one.
    setStuck(null);
    const { templates: from, standardCover: standard } = seed.current;
    setPlans(Object.fromEntries(key.split(",").map((leg) => [leg, planFrom(leg, from?.[leg], order, standard)])));
  }, [open, key, order]);

  if (!open || !plans) return null;
  // Through planAfter, so a provider change takes the carrier, the service and
  // the packaging with it instead of leaving three fields the new provider has
  // never heard of.
  const set = (leg) => (field, value) =>
    setPlans((previous) => ({ ...previous, [leg]: planAfter(previous[leg], field, value) }));

  const pair = legs.length > 1;

  const buy = async () => {
    setBusy(true);
    try {
      if (pair) {
        const { results } = await ordersApi.buyKits([order.number], true, {
          outbound_kit: planBody(plans.outbound_kit),
          inbound_items: planBody(plans.inbound_items),
        });
        if (!results[0]?.ok) throw new Error(results[0]?.reason || "The labels could not be bought");
      } else {
        await ordersApi.buyLabel(order.number, { leg: legs[0], ...planBody(plans[legs[0]]) });
      }
      toast.success(pair ? "Both labels bought. Print the pair before you pack it." : "Label bought");
      onDone();
      onClose();
    } catch (problem) {
      // A purchase refused because Stamps.com is not connected is the one
      // failure with an obvious next move, so it is offered here rather than
      // left as a toast somebody has to interpret. The message is the server's;
      // the button is the way out of it.
      //
      // The server says so as a fact on the body. This used to match a phrase in
      // the message, and the phrase it looked for was not in either sentence the
      // server sends, so the button never appeared once.
      if (problem.body?.canFallBack) setStuck(problem.message);
      else toast.error(problem.message);
    } finally {
      setBusy(false);
    }
  };

  // Every leg switched to EasyPost at once, which is what somebody means when
  // they press it. The dropdowns cascade, so the carrier, the service and the
  // packaging come with it rather than staying as Stamps codes EasyPost has
  // never heard of.
  const useEasyPost = () => {
    setPlans((previous) => Object.fromEntries(
      Object.entries(previous).map(([leg, plan]) => [leg, planAfter(plan, "provider", "easypost")])
    ));
    setStuck(null);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={pair ? "lg" : "md"}
      title={pair ? "Buy the kit, my way" : `Buy the ${LEG_TITLES[legs[0]].toLowerCase()}`}
      description={pair
        ? "Both halves, from the templates. Cover is ours and is recorded on the shipment, not bought from anybody."
        : "From the template for this leg. Cover is ours and is recorded on the shipment, not bought from anybody."}
      footer={(
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button onClick={buy} loading={busy}>{pair ? "Buy both labels" : "Buy label"}</Button>
        </>
      )}
    >
      {stuck && (
        <Alert tone="warning" className="mb-5" title="Stamps.com is not connected">
          <p className="mb-3">{stuck}</p>
          <Button size="sm" onClick={useEasyPost}>Buy through EasyPost instead</Button>
        </Alert>
      )}

      {/* The pair rule, said once, where somebody is about to break it. Two
          labels bought minutes apart is how a return label ends up in the wrong
          mailer, so buying one half names the half that will be missing rather
          than letting somebody discover it at the packing bench. */}
      {!pair && KIT_LEGS.some((entry) => entry.leg === legs[0]) && (
        <Alert tone="warning" className="mb-5">
          This buys one half of the kit. The{" "}
          {legs[0] === "outbound_kit" ? "prepaid return label" : "label for the outside of the mailer"}{" "}
          will still be missing, and a mailer packed without both is the mistake the pair exists to stop.
        </Alert>
      )}

      <div className={clsx("grid gap-6", pair && "sm:grid-cols-2")}>
        {legs.map((leg) => (
          <section key={leg} className="space-y-3 min-w-0">
            {pair && <p className="eyebrow border-b border-rule-soft pb-2">{LEG_TITLES[leg]}</p>}
            <LabelFields plan={plans[leg]} set={set(leg)} wide={!pair} />
          </section>
        ))}
      </div>
    </Modal>
  );
}

// Locked once a carrier has it. The same list the server enforces; this only
// decides whether to draw the control, and the server decides whether to obey.
const COVER_LOCKED = ["in_transit", "out_for_delivery", "delivered", "returned", "failure"];

function Cover({ shipment, order, onChanged }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(shipment.insuredValue ?? ""));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const locked = COVER_LOCKED.includes(shipment.status);
  const history = shipment.insuredHistory || [];

  const save = async () => {
    setSaving(true);
    try {
      await ordersApi.setInsurance(order.number, shipment.leg, {
        value: Number(value),
        note: note || undefined,
      });
      toast.success("Cover updated");
      setEditing(false);
      setNote("");
      onChanged();
    } catch (problem) {
      toast.error(problem.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span className="inline-flex flex-wrap items-baseline gap-2">
        <span className="num">{money(shipment.insuredValue || 0)}</span>
        {locked ? (
          <span className="hint">fixed, it is moving</span>
        ) : (
          <button type="button" className="link text-[12.5px] text-muted hover:text-ink" onClick={() => setEditing(true)}>
            Change
          </button>
        )}
        {history.length > 1 && (
          <span className="hint" title={history.map((row) => `${money(row.value)} by ${row.byName || "someone"}`).join("\n")}>
            {history.length} changes
          </span>
        )}
        {history.length > 0 && (
          <span className="hint">
            set by {history[history.length - 1].byName || "the system"}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="block mt-1">
      <span className="flex flex-wrap items-end gap-2">
        <Input
          label="Covered for"
          type="number"
          prefix="$"
          className="pl-6 w-32"
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <Input
          label="Why"
          placeholder="They called, more than described"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          wrapClassName="min-w-[12rem] flex-1"
        />
        <Button size="sm" onClick={save} loading={saving}>Save</Button>
        <Button size="sm" variant="quiet" onClick={() => { setEditing(false); setValue(String(shipment.insuredValue ?? "")); }}>
          Cancel
        </Button>
      </span>
      <span className="block hint mt-1">
        This is our own cover, not the carrier's. It goes on the record with your name.
      </span>
    </span>
  );
}

function Leg({ shipment, label, note, actions, order, onChanged }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 py-3 first:pt-0 last:pb-0 border-b border-rule-soft last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Truck size={16} className="text-bronze shrink-0" />
          <p className="font-semibold text-[14px] text-ink">{label}</p>
          {shipment && <Tag tone={shipment.status === "delivered" ? "positive" : "neutral"}>{shipment.status.replace(/_/g, " ")}</Tag>}
        </div>

        {shipment?.byHand && !shipment.shippedAt && (
          <Alert tone="info" className="mt-2">
            <p className="mb-2">
              This one is not bought through an API. Stamps.com is not connected, and EasyPost does not sell
              First Class Mail on a flat, so the postage is made on stamps.com and this is the note that it
              is waiting. Copy the address, buy it there, then mark it posted.
            </p>
            <pre className="num text-[12.5px] leading-snug bg-surface border border-rule p-2 whitespace-pre-wrap">
              {addressLines(order.customer)}
            </pre>
          </Alert>
        )}

        {shipment ? (
          <dl className="mt-2 text-[13px] space-y-1">
            <Row label="Carrier">{`${shipment.carrier} ${shipment.service || ""}`}</Row>
            <Row label="Tracking">
              {shipment.tracking
                ? <span className="num">{shipment.tracking}</span>
                : shipment.byHand ? <span className="text-muted">None. First class on a flat does not carry one.</span> : "—"}
            </Row>
            <Row label="Covered for"><Cover shipment={shipment} order={order} onChanged={onChanged} /></Row>
            <Row label="Cost">{shipment.cost ? moneyExact(shipment.cost) : "—"}</Row>
            <Row label="Shipped">{shipment.shippedAt ? day(shipment.shippedAt) : "—"}</Row>
            <Row label="Delivered">{shipment.deliveredAt ? dayTime(shipment.deliveredAt) : "—"}</Row>
          </dl>
        ) : (
          <p className="hint mt-1">{note}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2 shrink-0">{actions}</div>
    </div>
  );
}

function Shipping({ order, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [attaching, setAttaching] = useState(null);
  const [tracking, setTracking] = useState("");
  // Which legs the buy dialog is open for, as a list: one for a single label,
  // both halves for a kit. The templates come from settings and are only what
  // the dialog opens with.
  const [buying, setBuying] = useState(null);
  const { data: settings } = useAsync(() => adminApi.settings(), []);
  const templates = settings?.settings?.shipping?.templates || {};
  const standardCover = settings?.settings?.coverage?.standard;

  const find = (leg) => order.shipments?.find((entry) => entry.leg === leg && !entry.voidedAt);
  const outbound = find("outbound_kit");
  const inbound = find("inbound_items");
  const back = find("return_items");
  const addressed = Boolean(order.customer?.address?.line1 && order.customer?.address?.postal);

  const run = async (job, message) => {
    setBusy(true);
    try {
      const result = await job();
      if (result) onChanged(result);
      else onChanged(await ordersApi.get(order.number));
      toast.success(message);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  // Both halves, one press, no dialog. The queue at /admin/kits does the same
  // thing to a whole morning's worth at once.
  const buyKit = () => run(async () => {
    const { results } = await ordersApi.buyKits([order.number]);
    const answer = results[0];
    if (!answer?.ok) throw new Error(answer?.reason || "The labels could not be bought");
    return null;
  }, "Kit labels sorted. Print what there is before you pack it.");

  const refresh = (leg) => run(() => ordersApi.refreshTracking(order.number, leg), "Pulled the latest scans");
  const markPosted = (leg) => run(() => ordersApi.markPosted(order.number, leg), "Marked as posted");
  const voidIt = (leg) => run(() => ordersApi.voidLabel(order.number, leg), "Label voided and a refund requested");

  const attach = () => run(async () => {
    const updated = await ordersApi.attachTracking(order.number, { leg: attaching, tracking, carrier: "FedEx" });
    setAttaching(null);
    setTracking("");
    return updated;
  }, "Tracking attached");

  const printPair = () => window.open(`/admin/kits/print?numbers=${order.number}`, "_blank", "noopener");

  return (
    <div className="space-y-4">
      <Panel className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 pb-3 mb-1 border-b border-rule">
          <div>
            <p className="font-semibold text-[14.5px] text-ink">The kit</p>
            <p className="hint">
              {outbound?.byHand
                ? "Bought together. The outside label is made by hand on stamps.com, so printing gives you the return label only."
                : "Bought together. Printing gives you the return label first, then the one for the outside."}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {outbound && inbound ? (
              <Button size="sm" onClick={printPair}><Printer size={14} /> Print the pair</Button>
            ) : (
              <>
                <Button size="sm" onClick={buyKit} loading={busy} disabled={!addressed}>
                  <Plus size={14} /> Buy both labels
                </Button>
                {/* The ordinary path is the button beside this one: the pair,
                    from the templates, nobody asked anything. This is the way
                    out of that for the order going in a box, and it still buys
                    both halves at once. */}
                <Button size="sm" variant="quiet" onClick={() => setBuying(KIT_LEGS.map((entry) => entry.leg))} disabled={!addressed}>
                  Buy them my way
                </Button>
              </>
            )}
          </div>
        </div>

        {!addressed && (
          <Alert tone="warning" className="my-3">
            There is no full mailing address on this order, so nothing can be bought yet.
          </Alert>
        )}

        {KIT_LEGS.map(({ leg, label, note }) => {
          const shipment = find(leg);
          return (
            <Leg
              key={leg}
              shipment={shipment}
              label={label}
              note={note}
              order={order}
              onChanged={onChanged}
              actions={shipment ? (
                <>
                  {/* A leg made by hand has no label to open and nothing for a
                      carrier to refresh until somebody records a number. What it
                      has instead is the address and a way to say it went out. */}
                  {shipment.byHand ? (
                    <>
                      <CopyButton value={addressLines(order.customer)} label="Copy the address" />
                      {!shipment.shippedAt && (
                        <Button size="sm" onClick={() => markPosted(leg)} loading={busy}>
                          <Check size={14} /> Mark it posted
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setAttaching(leg)}>Add a number</Button>
                    </>
                  ) : (
                    <>
                      {shipment.labelUrl && (
                        <a href={shipment.labelUrl} target="_blank" rel="noopener noreferrer" className="btn btn-quiet btn-sm">
                          Label <ArrowUpRight size={14} />
                        </a>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => refresh(leg)}><Refresh size={14} /> Refresh</Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => voidIt(leg)}>Void</Button>
                </>
              ) : (
                // Always offered, not only after a void. The pair is the default
                // and the two buttons above say so, but a leg on its own is a
                // real thing to need: a replacement for one half, or a kit going
                // out today with the return label to follow. The dialog says
                // which half will be missing.
                <>
                  <Button size="sm" variant="quiet" onClick={() => setBuying([leg])} disabled={!addressed}>
                    Buy just this one
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setAttaching(leg)}>Attach a number</Button>
                </>
              )}
            />
          );
        })}
      </Panel>

      <Panel className="p-4">
        <Leg
          shipment={back}
          label="Items back to the customer"
          note="Bought when somebody declines. Packed as it arrived, at our cost."
          order={order}
          onChanged={onChanged}
          actions={back ? (
            <>
              {back.labelUrl && (
                <a href={back.labelUrl} target="_blank" rel="noopener noreferrer" className="btn btn-quiet btn-sm">
                  Label <ArrowUpRight size={14} />
                </a>
              )}
              <Button size="sm" variant="ghost" onClick={() => refresh("return_items")}><Refresh size={14} /> Refresh</Button>
              <Button size="sm" variant="ghost" onClick={() => voidIt("return_items")}>Void</Button>
            </>
          ) : (
            <>
              <Button size="sm" onClick={() => setBuying(["return_items"])} disabled={!addressed}>
                <Plus size={14} /> Buy the label
              </Button>
              <Button size="sm" variant="quiet" onClick={() => setAttaching("return_items")}>Attach a number</Button>
            </>
          )}
        />
      </Panel>

      <BuyLabelDialog
        order={order}
        legs={buying || []}
        templates={templates}
        standardCover={standardCover}
        open={Boolean(buying)}
        onClose={() => setBuying(null)}
        onDone={onChanged}
      />

      {[outbound, inbound, back].filter((shipment) => shipment?.scans?.length).map((shipment) => (
        <Panel key={shipment.leg} className="p-4">
          <details>
            <summary className="text-[13px] text-muted cursor-pointer select-none">
              {shipment.tracking} scans ({shipment.scans.length})
            </summary>
            <ul className="mt-2 space-y-1.5 border-l border-rule pl-3">
              {[...shipment.scans].reverse().map((scan, index) => (
                <li key={index} className="text-[12.5px]">
                  <span className="text-ink-soft">{scan.message}</span>
                  <span className="text-faint"> {[scan.city, scan.state].filter(Boolean).join(", ")} · {dayTime(scan.at)}</span>
                </li>
              ))}
            </ul>
          </details>
        </Panel>
      ))}

      <Modal
        open={Boolean(attaching)}
        onClose={() => setAttaching(null)}
        title="Attach a tracking number"
        description="For a label bought somewhere else. We will register it for scan updates."
        footer={<><Button variant="quiet" onClick={() => setAttaching(null)}>Cancel</Button><Button onClick={attach} loading={busy} disabled={tracking.length < 6}>Attach</Button></>}
      >
        <Input label="Tracking number" className="num" value={tracking} onChange={(event) => setTracking(event.target.value)} autoFocus />
      </Modal>
    </div>
  );
}
////////// 6. LOG //////////

const EVENT_ICON = {
  status: Flag, note: Note, message: Message, call: Phone, shipping: Truck,
  media: Camera, offer: Dollar, payout: Dollar, identity: IdCard, agreement: Check,
  system: Refresh, error: Flag,
};

function Log({ events }) {
  if (!events?.length) return <Empty title="Nothing logged yet" icon={Note} />;

  return (
    <ol className="border-l border-rule ml-2">
      {events.map((event) => {
        const Glyph = EVENT_ICON[event.type] || Note;
        return (
          <li key={event._id} className="relative pl-7 pb-6 last:pb-0">
            <span className={clsx(
              "absolute -left-[13px] top-0 w-[26px] h-[26px] rounded-full border flex items-center justify-center bg-paper",
              event.type === "error" ? "border-critical text-critical" : "border-rule text-muted"
            )}>
              <Glyph size={13} />
            </span>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <p className="text-[14px] text-ink font-medium">{event.title}</p>
              {event.visibility === "customer" && <Tag tone="bronze">Customer sees this</Tag>}
            </div>
            {event.detail && <p className="hint mt-1 leading-relaxed whitespace-pre-line">{event.detail}</p>}
            <p className="text-[12px] text-faint mt-1">
              {dayTime(event.at)} &middot; {event.actor?.name || "System"}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

////////// 7. PAGE //////////

export default function OrderDetail() {
  const { number } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();

  const { data: order, loading, error, reload, setData } = useAsync(() => ordersApi.get(number), [number]);
  // The tab is in the URL so the orders list can send somebody straight to the
  // messages or the labels, which is the whole point of the row actions there.
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (next) => setParams(next === "overview" ? {} : { tab: next }, { replace: true });
  const [dialog, setDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  if (loading) return <Loading label={`Loading ${number}`} />;
  if (error) {
    return (
      <div className="p-8">
        <Alert tone="error">{error.message}</Alert>
        <Link to="/admin/orders" className="btn btn-quiet mt-4"><ArrowLeft size={16} /> Back to orders</Link>
      </div>
    );
  }

  const next = NEXT_ACTION[order.status] || {};
  const merge = (updated) => setData((previous) => ({ ...previous, ...updated }));

  const runAction = async () => {
    if (next.action === "status") setDialog("status");
    else if (next.action === "payout") setDialog("payout");
    else if (next.action === "label") setTab("shipping");
    else if (next.action === "upload") setTab("media");
    else if (next.action === "refresh") {
      try {
        merge(await ordersApi.refreshTracking(order.number, order.status === "kit_sent" || order.status === "kit_delivered" ? "outbound_kit" : "inbound_items"));
        toast.success("Pulled the latest scans");
      } catch (problem) {
        toast.error(problem.message);
      }
    }
  };

  const remove = async () => {
    try {
      await ordersApi.remove(order.number);
      toast.success(`Deleted ${order.number}`);
      navigate("/admin/orders");
    } catch (problem) {
      toast.error(problem.message);
    }
  };

  const tabs = [
    { value: "overview", label: "Overview" },
    { value: "appraisal", label: "Appraisal", count: order.appraised?.length },
    { value: "media", label: "Media", count: order.media?.length },
    { value: "messages", label: "Messages", count: order.messages?.length },
    { value: "shipping", label: "Shipping", count: order.shipments?.filter((s) => !s.voidedAt).length },
    { value: "log", label: "Log", count: order.events?.length },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px]">
      <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-ink mb-4">
        <ArrowLeft size={15} /> Orders
      </Link>

      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-5 pb-5 border-b border-rule">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="display display-lg text-ink num">{order.number}</h1>
            <Tag tone={STATUS_TONE[order.status] || "neutral"}>{order.statusLabel}</Tag>
          </div>
          <p className="text-[15px] text-ink-soft mt-1.5">
            {order.customerName}
            {order.customer.address?.city ? ` · ${order.customer.address.city}, ${order.customer.address.state}` : ""}
          </p>
          <p className="hint mt-0.5">Updated {ago(order.updatedAt)}</p>
        </div>

        {/* Calling and emailing are one tap on a phone and the reason somebody
            opens this screen away from a desk, so they keep their own row. The
            next action is the widest thing on it. */}
        <div className="w-full sm:w-auto flex flex-wrap items-center gap-2">
          {order.customer.phone && (
            <a href={`tel:${order.customer.phone}`} className="btn btn-quiet btn-sm flex-1 sm:flex-none"><Phone size={15} /> Call</a>
          )}
          {order.customer.email && (
            <a href={`mailto:${order.customer.email}`} className="btn btn-quiet btn-sm flex-1 sm:flex-none"><Envelope size={15} /> Email</a>
          )}
          <Button size="sm" variant="quiet" onClick={() => setDialog("edit")} className="flex-1 sm:flex-none"><Pencil size={15} /> Edit</Button>
          <Button size="sm" variant="quiet" onClick={() => setDialog("status")} className="flex-1 sm:flex-none">Change status</Button>
          {next.action !== null && next.label && (
            <Button size="sm" onClick={runAction} className="w-full sm:w-auto order-first sm:order-none">{next.label}</Button>
          )}
          {can("orders.delete") && (
            <Button size="sm" variant="ghost" onClick={() => setDeleting(true)} aria-label="Delete order">
              <Trash size={15} />
            </Button>
          )}
        </div>
      </header>

      {/* Unmissable on purpose. The pricing on an affiliate order is worked out
          differently, so anybody who opens one has to know before they start
          quoting rather than after. */}
      {order.affiliate?.code && (
        <div className="mt-4 border-l-2 border-bronze bg-bronze-wash px-4 py-3">
          <p className="text-[14px] font-semibold text-bronze-dark">
            Affiliate order &middot; {order.affiliate.name}
            <span className="tag tag-bronze ml-2 num align-middle">{order.affiliate.code}</span>
          </p>
          <p className="text-[13px] text-ink-soft mt-1 leading-snug">
            {order.affiliate.commission?.amount > 0
              ? <>Commission of <strong>{money(order.affiliate.commission.amount)}</strong> {order.affiliate.commission.paidAt ? `was paid ${ago(order.affiliate.commission.paidAt)}` : "is owed on this order"}.</>
              : <>They earn {order.affiliate.commission?.percent || 0}% of whatever this customer is paid{order.affiliate.commission?.cap > 0 ? `, up to ${money(order.affiliate.commission.cap)}` : ""}. Worked out when the payout is recorded.</>}
            {" "}
            <span className="text-faint">Attached {order.affiliate.by === "link" ? "by their link" : `by ${order.affiliate.by}`}, {ago(order.affiliate.at)}. The customer sees none of this.</span>
          </p>
        </div>
      )}

      {next.hint && next.action !== null && (
        <p className="hint mt-3">{next.hint}</p>
      )}

      {/* Edge to edge on a phone: a strip that scrolls should start and end at
          the screen edge, or the last tab looks like the last tab. */}
      <Tabs tabs={tabs} value={tab} onChange={setTab} className="mt-5 mb-6 -mx-4 px-4 sm:mx-0 sm:px-0" />

      {tab === "overview" && <Overview order={order} onChanged={reload} onEdit={() => setDialog("edit")} />}
      {tab === "appraisal" && <Appraisal order={order} onSaved={(updated) => { merge(updated); reload(); }} />}
      {tab === "media" && <Media order={order} onChanged={reload} />}
      {tab === "messages" && <OrderMessages order={order} onChanged={reload} />}
      {tab === "shipping" && <Shipping order={order} onChanged={(updated) => { merge(updated); reload(); }} />}
      {tab === "log" && <Log events={order.events} />}

      <EditDialog order={order} open={dialog === "edit"} onClose={() => setDialog(null)} onDone={(updated) => { merge(updated); reload(); }} />
      <StatusDialog order={order} open={dialog === "status"} onClose={() => setDialog(null)} onDone={(updated) => { merge(updated); reload(); }} />
      <PayoutDialog order={order} open={dialog === "payout"} onClose={() => setDialog(null)} onDone={(updated) => { merge(updated); reload(); }} />

      <Modal
        open={deleting}
        onClose={() => setDeleting(false)}
        title={`Delete ${order.number}?`}
        description="This removes the order, its log and its files permanently. There is no undo."
        footer={<><Button variant="quiet" onClick={() => setDeleting(false)}>Cancel</Button><Button variant="danger" onClick={remove}>Delete permanently</Button></>}
      >
        <Alert tone="error">
          {order.customerName} and everything recorded about their order will be gone. Cancel it instead if you
          only want it out of the queue.
        </Alert>
      </Modal>
    </div>
  );
}
